import { observer } from 'mobx-react-lite'
import { type RefObject, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import {
  Button,
  IconButton,
  InputCheckbox,
  TextareaWithLabel,
  TextFieldWithLabel,
  Typography,
} from 'shared/ui'
import { Label } from 'shared/ui/Label'
import {
  LINK_CARRIER_AND_CTIA_GUIDELINES,
  LINK_COMPLIANCE_LEARN_MORE_LINK,
} from 'shared/constants/links'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { LearnMoreLink } from 'pages/settings/ui/LearnMoreLink/LearnMoreLink'
import { useLocalNumbersStepsContext } from 'pages/settings/pages/compliance/pages/localNumbers/context/localNumbersStepsContext'
import {
  sampleMessageMaxTextLength,
  sampleMessageMinTextLength,
} from 'pages/settings/pages/compliance/pages/localNumbers/pages/Messages/store/MessagesStore'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { RegistrationErrorAlert } from 'pages/settings/pages/compliance/pages/localNumbers/ui/RegistrationErrorAlert/RegistrationErrorAlert'
import { AiCheckerNextButtons } from 'pages/settings/pages/compliance/ui/AiCheckerNextButton/AiCheckerNextButtons'

import styles from './styles.module.scss'

export const Messages = observer(() => {
  const {
    messagesStore,
    stepLoading,
    aiSidebarStore: {
      isAiCheckrWillUse,
      isLoading: aiLoading,
      isSuccessButton,
      isErrorButton,
      isContinueWithIssues,
    },
  } = useLocalNumbersStepsContext()
  const { featureFlagsStore } = useEntitiesContext()

  const {
    includeEmbeddedLinks,
    includePhoneNumbers,
    includeAgeGatedContent,
    sampleMessage1,
    sampleMessage2,
    isStep2Disabled,
    campaignMassagesNextClick,
    setIncludeEmbeddedLinks,
    setIncludePhoneNumbers,
    setIncludeAgeGatedContent,
    setSampleMessage1,
    setSampleMessage2,
    openSampleMessageModal,
    aiGenerationStore: { isMessageGenerated, isMessage2Generated, messageLoading, message2Loading },
    generateAISampleMessage,
    getMessageGenerationText,
    resetField,
    shouldResetMessageErrors,
    shouldResetMessage2Errors,
    errorPrivacyPolicyUrl,
    errorTermsUrl,
    privacyPolicyUrl,
    termsUrl,
    setPrivacyPolicyUrl,
    setTermsUrl,
  } = messagesStore

  const { state } = useLocation()
  const { salesmsg_compliance_ai_v2 } = featureFlagsStore

  const [isFocusedCheckbox, setIsFocusedCheckbox] = useState(false)

  const message1Ref = useRef<HTMLTextAreaElement>(null)
  const message2Ref = useRef<HTMLTextAreaElement>(null)
  const includeEmbeddedLinksRef = useRef<HTMLInputElement>(null)
  const includePhoneNumbersRef = useRef<HTMLInputElement>(null)
  const includeAgeGatedContentRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const wait = (ref: RefObject<HTMLTextAreaElement | HTMLInputElement>) =>
      setTimeout(() => {
        ref.current?.blur()
        setIsFocusedCheckbox(false)
      }, 1000)

    if (state?.focusField) {
      switch (state.focusField) {
        case 'sampleMessagesData.sampleMessage1':
          message1Ref.current?.focus()

          wait(message1Ref)
          break
        case 'sampleMessagesData.sampleMessage2':
          message2Ref.current?.focus()

          wait(message2Ref)
          break
        case 'sampleMessagesData.includeEmbeddedLinks':
          includeEmbeddedLinksRef.current?.focus()
          setIsFocusedCheckbox(true)

          wait(includeEmbeddedLinksRef)
          break
        case 'sampleMessagesData.includePhoneNumbers':
          includePhoneNumbersRef.current?.focus()
          setIsFocusedCheckbox(true)

          wait(includePhoneNumbersRef)
          break
        case 'sampleMessagesData.includeAgeGatedContent':
          includeAgeGatedContentRef.current?.focus()
          setIsFocusedCheckbox(true)

          wait(includeAgeGatedContentRef)
          break
        default:
          break
      }
    }
  }, [state])

  return (
    <div>
      <PageDescription>
        Provide 2 examples of messages that you will be sending with this campaign. This sample will
        be used by carriers to help identify your traffic.
        <LearnMoreLink href={LINK_COMPLIANCE_LEARN_MORE_LINK}>Learn more</LearnMoreLink>
      </PageDescription>

      <RegistrationErrorAlert />

      <div className={styles.messagesCheckboxesWrap}>
        <Label label={{ text: 'Message contents (select if applicable)' }} marginBottom={'8px'} />
        <InputCheckbox
          ref={includeEmbeddedLinksRef}
          label='Messages will include embedded links.'
          checked={includeEmbeddedLinks}
          onChecked={setIncludeEmbeddedLinks}
          questionTooltipProps={{
            width: 258,
            type: 'description',
            label: 'If checked, make sure to include a link in the sample message.',
            desc: 'Failure to do so may prevent the campaign from being approved.',
          }}
          checkboxClassName={isFocusedCheckbox ? styles.focusCheckbox : ''}
        />
        <InputCheckbox
          ref={includePhoneNumbersRef}
          label='Messages will include phone numbers.'
          checked={includePhoneNumbers}
          onChecked={setIncludePhoneNumbers}
          questionTooltipProps={{
            width: 258,
            type: 'description',
            label: 'If checked, make sure to include a phone number in the sample message.',
            desc: 'Failure to do so may prevent the campaign from being approved.',
          }}
          checkboxClassName={isFocusedCheckbox ? styles.focusCheckbox : ''}
        />
        <InputCheckbox
          ref={includeAgeGatedContentRef}
          label={
            <div>
              Messages include age-gated content.
              <a
                href={LINK_CARRIER_AND_CTIA_GUIDELINES}
                target='_blank'
                rel='noreferrer'
                className={styles.guidelinesLink}
              >
                <Typography
                  ariaLabel='TermsAndPrivacy_link'
                  variant='body-md-regular'
                  color='var(--content-brand-primary)'
                >
                  See Carrier and CTIA guidelines
                </Typography>
              </a>
            </div>
          }
          checked={includeAgeGatedContent}
          onChecked={setIncludeAgeGatedContent}
          checkboxClassName={isFocusedCheckbox ? styles.focusCheckbox : ''}
        />
      </div>

      <TextareaWithLabel
        ref={message1Ref}
        className={styles.sampleMessageDesc}
        value={sampleMessage1}
        limit={sampleMessageMaxTextLength}
        minLimit={sampleMessageMinTextLength}
        errorType='bottomText'
        placeholder='Enter the most common message you send to your contacts'
        onChange={setSampleMessage1}
        shouldResetErrors={shouldResetMessageErrors}
        labelProps={{
          marginBottom: 0,
          label: {
            text: 'Sample message #1',
            rightText: 'See example',
            rightNode: (
              <Button
                onClick={openSampleMessageModal}
                text={'See example'}
                size='small'
                typeBtn='informative'
              />
            ),
          },
        }}
        bottomNode={{
          leftNode: salesmsg_compliance_ai_v2 ? (
            <Button
              onClick={() => generateAISampleMessage('message1')}
              text={messageLoading ? 'Generating...' : getMessageGenerationText('message1')}
              size='small'
              typeBtn='informative'
              icon='stars'
              iconProps={{
                color: 'var(--content-brand-primary)',
              }}
              color={'var(--action-contained-primary-normal)'}
              disabled={messageLoading}
            />
          ) : null,
          rightNode:
            salesmsg_compliance_ai_v2 && isMessageGenerated ? (
              <IconButton
                iconProps={{ fontSize: 16 }}
                variant={'icon'}
                color={'tertiary'}
                size={'small'}
                icon={'back'}
                ariaLabel='Reset'
                onClick={() => resetField('message1')}
                tooltipProps={{
                  label: 'Reset to previous version',
                  placement: 'top',
                }}
              />
            ) : null,
        }}
      />

      <TextareaWithLabel
        ref={message2Ref}
        className={styles.sampleMessageDesc}
        value={sampleMessage2}
        limit={sampleMessageMaxTextLength}
        minLimit={sampleMessageMinTextLength}
        errorType='bottomText'
        placeholder='Enter the second most common message you send to your contacts'
        onChange={setSampleMessage2}
        shouldResetErrors={shouldResetMessage2Errors}
        labelProps={{
          marginBottom: 0,
          label: {
            text: 'Sample message #2',
            rightNode: (
              <Button
                onClick={openSampleMessageModal}
                text={'See example'}
                size='small'
                typeBtn='informative'
              />
            ),
          },
        }}
        bottomNode={{
          leftNode: salesmsg_compliance_ai_v2 ? (
            <Button
              onClick={() => generateAISampleMessage('message2')}
              text={message2Loading ? 'Generating...' : getMessageGenerationText('message2')}
              size='small'
              typeBtn='informative'
              icon='stars'
              iconProps={{
                color: 'var(--content-brand-primary)',
              }}
              color={'var(--action-contained-primary-normal)'}
              disabled={message2Loading}
            />
          ) : null,
          rightNode:
            salesmsg_compliance_ai_v2 && isMessage2Generated ? (
              <IconButton
                iconProps={{ fontSize: 16 }}
                variant={'icon'}
                color={'tertiary'}
                size={'small'}
                icon={'back'}
                ariaLabel='Reset'
                onClick={() => resetField('message2')}
                tooltipProps={{
                  label: 'Reset to previous version',
                  placement: 'top',
                }}
              />
            ) : null,
        }}
      />
      <TextFieldWithLabel
        marginTop={3}
        errorMessage={errorPrivacyPolicyUrl}
        labelProps={{
          marginBottom: 4,
          label: {
            text: 'Privacy Policy URL (optional)',
          },
        }}
        textFieldProps={{
          value: privacyPolicyUrl,
          onChange: setPrivacyPolicyUrl,
          InputProps: {
            placeholder: 'e.g. https://www.example.com/',
          },
          className: styles.textField,
        }}
      />
      <TextFieldWithLabel
        marginTop={3}
        marginBottom={0.5}
        errorMessage={errorTermsUrl}
        labelProps={{
          marginBottom: 4,
          label: {
            text: 'Terms and Conditions URL (optional)',
          },
        }}
        textFieldProps={{
          value: termsUrl,
          onChange: setTermsUrl,
          InputProps: {
            placeholder: 'e.g. https://www.example.com/',
          },
          className: styles.textField,
        }}
      />

      <div className={styles.stickyActions}>
        {isAiCheckrWillUse ? (
          <AiCheckerNextButtons
            loading={aiLoading}
            disabled={isStep2Disabled || aiLoading}
            text='Check and continue'
            onClick={() => campaignMassagesNextClick(true)}
            icon='stars'
            isSuccess={isSuccessButton}
            isError={isErrorButton}
            isContinueWithIssues={isContinueWithIssues}
            onContinueWithIssuesClick={() => campaignMassagesNextClick()}
            isContinueWithIssuesLoading={stepLoading}
          />
        ) : (
          <NextButton
            loading={stepLoading}
            disabled={isStep2Disabled || stepLoading}
            text='Next'
            onClick={() => campaignMassagesNextClick()}
          />
        )}
      </div>
    </div>
  )
})
